import { mapState, mapGetters, mapActions } from 'vuex';

/**
 * checkImage verifies that the provided image src is valid.
 */
const checkImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.src = src;
  img.onload = resolve;
  img.onerror = reject;
});

export default {
  props: {
    id: null,
    type: null,
    toggle: null,
    group: null,
    parent: null,
    selfPositioned: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLooping: false,
      useFallback: false,
    };
  },
  computed: {
    ...mapState({
      selectedElements: (state) => state.templates.selectedElements,
    }),
    isSelected() {
      return this.selectedElements.indexOf(this.id) !== -1;
    },

    ...mapGetters('graphicsTemplate', [
      'getBasicStyles',
      'getTransformStyles',
      'getAnimationStyles',
      'getBorderRadiusStyles',
      'getBackgroundStyles',
      'getBorderStyles',
      'getBoxShadowStyles',
      'getBlurStyles',
      'getGapStyles',
      'getLoopingClass',
      'getElementValueByProp',
      'getCanvas',
      'getSafeArea',
      'getElementById',
      'getElementGroupMap',
      'getTextShadowStyles',
      'getTextBackgroundStyles',
      'getDataValue',
      'getDataProvider',
      'isDataProvider',
      'getBackgroundImageUrl',
      'getAllParents',
      'getAbsolutePosition',
      'isPropertyMapped',
      'getPaddingStyles',
    ]),

    ...mapGetters('panAndZoom', { scaleFactor: 'getScaleFactor' }),

    loopingClass() {
      if (this.isLooping) {
        return this.getLoopingClass(this.id);
      }

      return '';
    },
    textShadowStyles() {
      return this.getTextShadowStyles(this.id);
    },
    textBackgroundStyles() {
      return this.getTextBackgroundStyles(this.id);
    },
    basicStyles() {
      return this.getBasicStyles(this.id);
    },
    transformStyles() {
      return this.getTransformStyles(this.id);
    },
    animationStyles() {
      return this.getAnimationStyles(this.id, this.isLooping, this.toggle);
    },
    borderRadiusStyles() {
      return this.getBorderRadiusStyles(this.id);
    },
    backgroundStyles() {
      return this.getBackgroundStyles(this.id, this.useFallback);
    },
    borderStyles() {
      return this.getBorderStyles(this.id);
    },
    boxShadowStyles() {
      return this.getBoxShadowStyles(this.id);
    },
    blurStyles() {
      return this.getBlurStyles(this.id);
    },
    gapStyles() {
      return this.getGapStyles(this.id);
    },
    elementValueByProp() {
      return this.getElementValueByProp(this.id);
    },
    element() {
      return this.getElementById(this.id);
    },
    parents() {
      return this.getAllParents(this.id);
    },
    styles() {
      return this.getElementValueByProp(this.id, 'styles');
    },
    animation() {
      return this.getElementValueByProp(this.id, 'animation');
    },
    animationInStyle() {
      return this.getElementValueByProp(this.id, 'animationInStyle');
    },
    animationOutStyle() {
      return this.getElementValueByProp(this.id, 'animationOutStyle');
    },
    locked() {
      return this.getElementValueByProp(this.id, 'locked');
    },
    aspectLock() {
      return this.getElementValueByProp(this.id, 'aspectLock');
    },
    cssClasses() {
      return this.getElementValueByProp(this.id, 'cssClasses') ?? '';
    },
    animationIn() {
      return this.animation ? this.animationInStyle : '';
    },
    animationOut() {
      return this.animation ? this.animationOutStyle : '';
    },
    animationOutDelay() {
      return this.getElementValueByProp(this.id, 'animationOutDelay');
    },
    animationOutDuration() {
      return this.getElementValueByProp(this.id, 'animationOutDuration');
    },
    verticalAlign() {
      return this.getElementValueByProp(this.id, 'verticalAlign');
    },
    paddingStyles() {
      return this.getPaddingStyles(this.id);
    },
    leaveDuration() {
      const parentDuration = this.getElementById[this.parent]
        ? this.parent.animationOutDelay + this.parent.animationOutDuration
        : 0;

      const elDuration = this.animationOutDelay + this.animationOutDuration;

      return parentDuration > elDuration ? parentDuration : elDuration;
    },
    backgroundImageUrl() {
      return this.getBackgroundImageUrl(this.id);
    },
    /**
     * The fallback background image is only used for data mapped images currently.
     * It ignores any data mapped image
     * and just returns whatever the user has defined as the fallback image.
     */
    fallbackBackgroundImageUrl() {
      return this.getElementValueByProp(this.id, 'backgroundImageUrl') || '';
    },
    // global state from templates
    runLoopingAnimations() {
      return this.$store.state.templates.runLoopAnimations;
    },
    imageMetaOptimizeApplied() {
      return this.hasMetaFlag(this.element?.imageMeta);
    },
    backgroundMetaOptimizeApplied() {
      return this.hasMetaFlag(this.element?.backgroundImageMeta);
    },
    dataProvider() {
      if (this.isDataProvider(this.id)) {
        return this.getElementById[this.id];
      }
      return this.getDataProvider(this.id);
    },
    labelOverride() {
      return this.dataProvider?.type === 'sportradar' && this.element.data?.isLabel;
    },

    /**
     * in order for draggable to not mangle our transforms we have to set them using gsap
     * this is a helper to track all of those in one place so we can apply them when needed
     * which is usually after translateDrag[0].enable() is called
     */
    tweenMaxTransforms() {
      return {
        rotation: this.element.rotate ?? 0,
        skewX: this.element.skewX ?? 0,
        skewY: this.element.skewY ?? 0,
        scaleX: this.element.scaleX ?? 1,
        scaleY: this.element.scaleY ?? 1,
      };
    },
  },
  methods: {
    ...mapActions('graphicsTemplate', [
      'setElementStylesValueByProp',
      'setOneElementValueByProp',
      'incrementElementPosition',
      'decrementElementPosition',
      'setElementVideoOptionsValueByProp',
      'deleteOneElementProp',
    ]),
    incrementPosition(positionType, stepSize) {
      this.incrementElementPosition({ value: { id: this.id, positionType, stepSize } });
    },
    decrementPosition(positionType, stepSize) {
      this.decrementElementPosition({ value: { id: this.id, positionType, stepSize } });
    },
    updateStyles(value, options) {
      return this.setElementStylesValueByProp({ value, options });
    },
    updateVideoOptions(value, options) {
      return this.setElementVideoOptionsValueByProp({ value, options });
    },
    updateElementProp(value, options) {
      return this.setOneElementValueByProp({ value, options });
    },
    hasMetaFlag(meta, key = 'optimize') {
      if (!meta || typeof meta !== 'object' || !key) {
        return false;
      }

      return Object.prototype.hasOwnProperty.call(meta, key) && meta[key] === true;
    },
    isMapped(property) {
      return this.isPropertyMapped(this.element.id, property);
    },
    checkImage() {
      if (
        this.backgroundImageUrl
        && this.fallbackBackgroundImageUrl
        && this.backgroundImageUrl !== this.fallbackBackgroundImageUrl
      ) {
        // We only need to verify the image if
        // we actually have a background image and a DIFFERENT fallback image
        checkImage(this.backgroundImageUrl)
          .then(() => {
            this.useFallback = false;
          })
          .catch(() => {
            this.useFallback = true;
          });
      }
    },

    afterEnter(el) {
      const loopingAnimation = this.getElementValueByProp(
        this.id,
        'loopingAnimation',
      );
      const loopingAnimationDuration = this.getElementValueByProp(
        this.id,
        'loopingAnimationDuration',
      );
      const loopingAnimationDelay = this.getElementValueByProp(
        this.id,
        'loopingAnimationDelay',
      );

      if (loopingAnimation && this.runLoopingAnimations) {
        clearInterval(this.loopInterval);

        this.loopInterval = setInterval(() => {
          if (el.classList.contains('in')) {
            el.classList.remove('in');
            el.classList.add('out');
          } else {
            el.classList.remove('out');
            el.classList.add('in');
          }
        }, loopingAnimationDuration + loopingAnimationDelay);

        this.isLooping = true;
      }
    },

    beforeLeave() {
      clearInterval(this.loopInterval);
      this.isLooping = false;
    },
  },
  watch: {
    backgroundImageUrl() {
      this.checkImage();
    },
  },
  mounted() {
    this.checkImage();
  },
};
